/**
 * Character Service
 *
 * Fetches character data from the main Arcane Circle platform
 * for display in the VTT (token tooltips, dice roll modifiers, etc.)
 */

export interface VTTCharacterStats {
  hp: { current: number; max: number; temp: number }
  ac: number
  speed: number
  initiative: number
  proficiencyBonus: number
}

export interface VTTCharacterAbility {
  score: number
  mod: number
}

export interface VTTCharacterSkill {
  mod: number
  proficient: boolean
}

/** Server's classification of the character's game system. Drives
 *  which HUD layout the client renders. */
export type SystemKind = 'd20' | 'burning-wheel' | 'generic'

/**
 * Infer a SystemKind from the raw `system` string.
 *
 * The platform's vtt-data formatter is the source of truth for `systemKind`,
 * but legacy responses (pre-formatter-unification) and any not-yet-updated
 * formatter omit it. Rather than blanket-default a missing value to 'd20' —
 * which makes a Call of Cthulhu or Blades sheet render the d20 HUD with zeroed
 * HP/AC — we recognize known non-d20 systems from the `system` string and
 * classify only those. Anything unknown (including all D&D/Pathfinder variants
 * and genuinely unrecognized systems) stays 'd20', preserving back-compat with
 * the historical global default.
 */
export function systemKindFromSystem(system: unknown): SystemKind {
  const s = String(system ?? '').toLowerCase()
  if (!s) return 'd20'
  // Burning Wheel gets its own attribute-pool layout.
  if (s.includes('burning-wheel') || s === 'bw') return 'burning-wheel'
  // Known non-d20 systems that should surface their customStats list rather
  // than the d20 HP/AC/ability HUD. Matched loosely so versioned ids
  // (coc7e, bitd, fate-core, …) all resolve.
  const nonD20 = ['coc', 'cthulhu', 'bitd', 'blades', 'fate', 'fae', 'pbta', 'apocalypse', 'vampire', 'wod', 'savage']
  if (nonD20.some((kind) => s.includes(kind))) return 'generic'
  // dnd5e-2014, dnd5e-2024, pf2e, pf1e, d20, and unknown → d20 HUD.
  return 'd20'
}

/** Per-stat row used by non-d20 systems. The renderer treats this as
 *  a flat key/value list (no derivation, no math). */
export interface VTTCustomStat {
  key: string
  label: string
  value: string | number
}

/** How the token is classified for sidebar grouping and initiative display. */
export type TokenClassification = 'player' | 'friendly' | 'enemy'

export interface VTTCharacterData {
  id: string
  playerId?: string
  playerName?: string
  name: string
  system: string
  /** Optional because legacy responses (pre-formatter-unification)
   *  don't include it. When missing, treat as 'd20' for back-compat. */
  systemKind?: SystemKind
  characterType: string | null
  imageUrl: string | null
  /** Canvas-token-specific image; falls back to imageUrl when null. */
  tokenImageUrl?: string | null
  level: number | null
  stats: VTTCharacterStats
  abilities: Record<string, VTTCharacterAbility>
  skills: Record<string, VTTCharacterSkill>
  saves: Record<string, VTTCharacterSkill>
  /** Present on non-d20 characters. Flat key/value list the renderer
   *  surfaces when the d20 HUD doesn't apply. */
  customStats?: VTTCustomStat[]
  /** NPC token fields — present on characters created as isTokenOnly.
   *  From the ac-mvp foundation build (1 of 2). */
  tokenClassification?: TokenClassification
  isMob?: boolean
  isTokenOnly?: boolean
}

/**
 * Lightweight character record returned by GET /api/characters.
 * Used by the NPC token modal's "link existing character" picker.
 */
export interface GMCharacterSummary {
  id: string
  name: string
  imageUrl: string | null
  tokenImageUrl: string | null
  system: string
  isTokenOnly?: boolean
  isMob?: boolean
  tokenClassification?: TokenClassification
  characterType?: string | null
}

export interface SessionCharactersResponse {
  sessionId: string
  gameId: string
  gameTitle: string
  gameSystem: string
  characters: VTTCharacterData[]
}

// Cache for character data (keyed by character ID)
const characterCache = new Map<string, VTTCharacterData>()

// Cache for session characters (keyed by session ID)
const sessionCharactersCache = new Map<string, VTTCharacterData[]>()

/**
 * Optional callback fired when a platform REST call returns 401 (the
 * Bearer JWT is no longer accepted). The consumer — App.tsx — registers
 * the same `onAuthExpired` it gives to socketService, so both transports
 * route auth expiry through one re-auth flow. Without this, REST fetches
 * would 401 silently and the cache would freeze on stale data while the
 * socket re-authenticated in the background.
 */
let onAuthExpired: (() => void) | null = null

export function setCharacterServiceAuthExpiredHandler(
  handler: (() => void) | null
): void {
  onAuthExpired = handler
}

/**
 * Internal — handle a 401 from any platform REST call. Wipes caches so
 * we don't serve stale data after re-auth and fires the registered
 * callback once. Multiple in-flight 401s collapse to a single callback
 * via the `notified` flag.
 */
let authExpiryNotified = false
function handleAuthExpired(label: string): void {
  console.warn(`[CharacterService] Auth expired during ${label} — clearing caches`)
  characterCache.clear()
  sessionCharactersCache.clear()
  if (!authExpiryNotified) {
    authExpiryNotified = true
    try {
      onAuthExpired?.()
    } finally {
      // Reset on next tick so a future expiry can re-notify
      setTimeout(() => {
        authExpiryNotified = false
      }, 1000)
    }
  }
}

/**
 * Get the main platform URL from environment or default.
 *
 * Must be the canonical `www` host: the apex `arcanecircle.games` 307-redirects
 * to `www.arcanecircle.games`, and on a cross-origin redirect the browser strips
 * the Authorization header — so a VTT character fetch aimed at the apex loses its
 * Bearer JWT mid-flight and 401s. Targeting `www` directly avoids the redirect.
 */
export function getMainPlatformUrl(): string {
  return import.meta.env.VITE_MAIN_PLATFORM_URL || 'https://www.arcanecircle.games'
}

/**
 * Build the URL for a character sheet on the main platform. Used by
 * the right-click token context menu's "Open character sheet" action.
 * Players land on the authed dashboard view; if they aren't signed in
 * they'll bounce through the login flow first.
 */
export function getCharacterSheetUrl(characterId: string): string {
  return `${getMainPlatformUrl()}/dashboard/characters/${encodeURIComponent(characterId)}`
}

/**
 * Coerce a possibly-wrapped numeric field into a bare number.
 *
 * The platform's dnd5e-2024 vtt-data formatter sometimes emits stat and
 * ability values as wrapper objects — e.g. `{ value: 15 }`,
 * `{ score: 15, modifier: 2 }`, or a speed as `{ walk: 30, fly: 0 }` —
 * instead of the plain numbers the older (2014) formatter returned. The HUD
 * components render these fields directly (`{stats.speed}`, `{ability.score}`),
 * so an object slipping through throws React's "Objects are not valid as a
 * React child" — and with no error boundary that blanked the whole page.
 * Flattening here at the ingest boundary keeps the render path object-free.
 */
function coerceNumber(input: unknown, fallback = 0): number {
  if (typeof input === 'number') return Number.isFinite(input) ? input : fallback
  if (typeof input === 'boolean') return input ? 1 : 0
  if (typeof input === 'string') {
    const n = Number(input)
    return Number.isFinite(n) ? n : fallback
  }
  if (input && typeof input === 'object') {
    const o = input as Record<string, unknown>
    // Common wrapper keys, in priority order. `walk` covers 2024 speed objects.
    for (const key of ['value', 'score', 'mod', 'modifier', 'total', 'current', 'walk', 'base']) {
      if (o[key] != null) return coerceNumber(o[key], fallback)
    }
  }
  return fallback
}

/** Read a modifier off an ability/skill object, preferring `mod` then `modifier`. */
function coerceMod(input: unknown): number {
  if (input && typeof input === 'object') {
    const o = input as Record<string, unknown>
    if (o.mod != null) return coerceNumber(o.mod)
    if (o.modifier != null) return coerceNumber(o.modifier)
  }
  return coerceNumber(input)
}

/** Flatten an image field that may arrive as `{ url }` / `{ src }` rather than a string. */
function coerceUrl(input: unknown): string | null {
  if (input == null) return null
  if (typeof input === 'string') return input
  if (typeof input === 'object') {
    const o = input as Record<string, unknown>
    const u = o.url ?? o.src ?? o.href ?? o.value
    return typeof u === 'string' ? u : null
  }
  return null
}

function normalizeSkillMap(map: unknown): Record<string, VTTCharacterSkill> {
  const out: Record<string, VTTCharacterSkill> = {}
  for (const [key, val] of Object.entries((map ?? {}) as Record<string, unknown>)) {
    const s = (val ?? {}) as Record<string, unknown>
    out[key] = {
      mod: typeof val === 'number' ? val : coerceMod(s),
      proficient: Boolean(s.proficient),
    }
  }
  return out
}

/**
 * Normalize a raw vtt-data payload into the primitive-only shape the HUD
 * renders. Tolerates both the 2014 (bare-number) and 2024 (wrapper-object)
 * formatter outputs so neither can crash the React tree.
 */
export function normalizeCharacterData(data: VTTCharacterData): VTTCharacterData {
  if (!data || typeof data !== 'object') return data
  const raw = data as Record<string, any>

  const rawStats = raw.stats ?? {}
  const rawHp = rawStats.hp ?? {}
  const stats: VTTCharacterStats = {
    hp: {
      current: coerceNumber(rawHp.current ?? rawHp.value),
      max: coerceNumber(rawHp.max),
      temp: coerceNumber(rawHp.temp),
    },
    ac: coerceNumber(rawStats.ac),
    speed: coerceNumber(rawStats.speed),
    initiative: coerceNumber(rawStats.initiative),
    proficiencyBonus: coerceNumber(rawStats.proficiencyBonus),
  }

  const abilities: Record<string, VTTCharacterAbility> = {}
  for (const [key, val] of Object.entries((raw.abilities ?? {}) as Record<string, unknown>)) {
    const a = (val ?? {}) as Record<string, unknown>
    const score = typeof val === 'number' ? val : coerceNumber(a.score ?? a.value)
    const hasMod =
      val != null && typeof val === 'object' && (a.mod != null || a.modifier != null)
    // Derive the modifier from the score when the formatter omits it.
    const mod = hasMod ? coerceMod(a) : Math.floor((score - 10) / 2)
    abilities[key] = { score, mod }
  }

  const customStats = Array.isArray(raw.customStats)
    ? raw.customStats.map((s: any) => ({
        key: String(s?.key ?? ''),
        label: String(s?.label ?? ''),
        value:
          typeof s?.value === 'string' || typeof s?.value === 'number'
            ? s.value
            : String(s?.value ?? ''),
      }))
    : raw.customStats

  return {
    ...(raw as VTTCharacterData),
    name: typeof raw.name === 'string' ? raw.name : String(raw.name ?? ''),
    // Trust the platform's systemKind when present; otherwise infer it from the
    // `system` string so non-d20 sheets aren't forced through the d20 HUD.
    systemKind: raw.systemKind ?? systemKindFromSystem(raw.system),
    level: raw.level == null ? null : coerceNumber(raw.level),
    imageUrl: coerceUrl(raw.imageUrl),
    tokenImageUrl: raw.tokenImageUrl === undefined ? undefined : coerceUrl(raw.tokenImageUrl),
    stats,
    abilities,
    skills: normalizeSkillMap(raw.skills),
    saves: normalizeSkillMap(raw.saves),
    customStats,
  }
}

/**
 * Fetch a single character's VTT data.
 *
 * Default behavior is cache-first; pass `{ bypassCache: true }` to force
 * a refetch (e.g. when the sheet modal opens, so the GM can see edits
 * made on the platform without a full reload).
 */
export async function fetchCharacterVTTData(
  characterId: string,
  token: string,
  options: { bypassCache?: boolean } = {}
): Promise<VTTCharacterData | null> {
  if (!options.bypassCache) {
    const cached = characterCache.get(characterId)
    if (cached) {
      return cached
    }
  }

  const platformUrl = getMainPlatformUrl()

  try {
    const response = await fetch(`${platformUrl}/api/characters/${characterId}/vtt-data`, {
      method: 'GET',
      // Cookie auth works in local dev (same-host different-port) and
      // is the fallback when the JWT-auth path isn't deployed yet.
      // Bearer is sent in parallel for environments that prefer it.
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (response.status === 401) {
      handleAuthExpired(`fetchCharacterVTTData(${characterId})`)
      return null
    }

    if (!response.ok) {
      console.warn(`[CharacterService] Failed to fetch character ${characterId}: ${response.status}`)
      return null
    }

    const data = normalizeCharacterData(await response.json())

    // Cache the result
    characterCache.set(characterId, data)

    return data
  } catch (error) {
    console.error(`[CharacterService] Error fetching character ${characterId}:`, error)
    return null
  }
}

/**
 * Edit a character's current HP on the canonical sheet (issue #135).
 *
 * Roll20/Foundry-style: pass `{ set }` for an absolute value or `{ delta }` for
 * a relative ±. The platform clamps to [0, max] and returns the new HP. Caller
 * is the owner or GM (the endpoint enforces it). On success the local cache is
 * invalidated so the next fetch (triggered by the broadcast) is fresh.
 */
export async function updateCharacterHp(
  characterId: string,
  body: { set: number } | { delta: number },
  token: string
): Promise<{ current: number; max: number; temp: number } | null> {
  const platformUrl = getMainPlatformUrl()
  try {
    const response = await fetch(`${platformUrl}/api/characters/${characterId}/hp`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
    if (response.status === 401) {
      handleAuthExpired(`updateCharacterHp(${characterId})`)
      return null
    }
    if (!response.ok) {
      console.warn(`[CharacterService] HP update failed for ${characterId}: ${response.status}`)
      return null
    }
    const data = (await response.json()) as {
      hp: { current: number; max: number; temp: number }
    }
    invalidateCharacter(characterId)
    return data.hp
  } catch (error) {
    console.error(`[CharacterService] Error updating HP for ${characterId}:`, error)
    return null
  }
}

/**
 * Edit a linked character's max HP, AC, and/or initiative (issue #657) —
 * the GM stat corrections a token needs after it's already been dropped
 * ("entered the HP total incorrectly"). Widens the same `/hp` endpoint
 * `updateCharacterHp` uses; any field omitted here is left untouched on the
 * sheet. The platform clamps `hp.current` down if a new `hpMax` drops below
 * it. GM-only — the endpoint enforces it and returns 403 for anyone else.
 * On success the local character cache is invalidated so the next fetch
 * (triggered by the caller's broadcast) is fresh.
 */
export async function updateCharacterStats(
  characterId: string,
  body: {
    set?: number
    hpMax?: number
    ac?: number
    initiative?: number
    // #766 — the platform's GM check only trusted a direct character.game
    // link, which a GM's own NPC/test tokens routinely don't have (linking
    // is a separate manual step). Pass sessionId whenever the caller has
    // one so the platform can independently verify GM + membership itself;
    // it grants nothing on its own. See ac-mvp's isSessionScopedGM.
    sessionId?: string
  },
  token: string
): Promise<{ hp: { current: number; max: number; temp: number }; ac?: number; initiative?: number } | null> {
  const platformUrl = getMainPlatformUrl()
  try {
    const response = await fetch(`${platformUrl}/api/characters/${characterId}/hp`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
    if (response.status === 401) {
      handleAuthExpired(`updateCharacterStats(${characterId})`)
      return null
    }
    if (!response.ok) {
      console.warn(`[CharacterService] Stats update failed for ${characterId}: ${response.status}`)
      return null
    }
    const data = (await response.json()) as {
      hp: { current: number; max: number; temp: number }
      ac?: number
      initiative?: number
    }
    invalidateCharacter(characterId)
    return data
  } catch (error) {
    console.error(`[CharacterService] Error updating stats for ${characterId}:`, error)
    return null
  }
}

/**
 * Re-classify a token's linked character on the canonical sheet (VTT #163).
 *
 * `tokenClassification` is character data, not a token field — it drives the
 * right-sidebar bank buckets (see utils/bankGroups.ts) and the enemy outline on
 * the canvas. Today it's only set at NPC creation; this is the re-classify
 * path. Mirrors updateCharacterHp: PATCH the platform character, invalidate the
 * cache on success so the broadcast-triggered refetch (character_classification
 * _updated → onCharacterNeedsRefresh) pulls the new value. GM-only; the endpoint
 * enforces edit permission.
 */
export async function updateCharacterClassification(
  characterId: string,
  classification: TokenClassification,
  token: string
): Promise<boolean> {
  const platformUrl = getMainPlatformUrl()
  try {
    const response = await fetch(`${platformUrl}/api/characters/${characterId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tokenClassification: classification }),
    })
    if (response.status === 401) {
      handleAuthExpired(`updateCharacterClassification(${characterId})`)
      return false
    }
    if (!response.ok) {
      console.warn(
        `[CharacterService] Classification update failed for ${characterId}: ${response.status}`
      )
      return false
    }
    invalidateCharacter(characterId)
    return true
  } catch (error) {
    console.error(
      `[CharacterService] Error updating classification for ${characterId}:`,
      error
    )
    return false
  }
}

/**
 * Fetch all characters for a session
 */
export async function fetchSessionCharacters(
  sessionId: string,
  token: string
): Promise<VTTCharacterData[]> {
  // Check cache first
  const cached = sessionCharactersCache.get(sessionId)
  if (cached) {
    return cached
  }

  const platformUrl = getMainPlatformUrl()

  try {
    const response = await fetch(`${platformUrl}/api/sessions/${sessionId}/characters`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (response.status === 401) {
      handleAuthExpired(`fetchSessionCharacters(${sessionId})`)
      return []
    }

    if (!response.ok) {
      console.warn(`[CharacterService] Failed to fetch session characters: ${response.status}`)
      return []
    }

    const raw: SessionCharactersResponse = await response.json()
    const characters = (raw.characters ?? []).map(normalizeCharacterData)

    // Cache session characters
    sessionCharactersCache.set(sessionId, characters)

    // Also cache individual characters
    for (const character of characters) {
      characterCache.set(character.id, character)
    }

    return characters
  } catch (error) {
    console.error(`[CharacterService] Error fetching session characters:`, error)
    return []
  }
}

/**
 * Get cached character data (returns null if not cached)
 */
export function getCachedCharacter(characterId: string): VTTCharacterData | null {
  return characterCache.get(characterId) || null
}

/**
 * Get cached session characters (returns empty array if not cached)
 */
export function getCachedSessionCharacters(sessionId: string): VTTCharacterData[] {
  return sessionCharactersCache.get(sessionId) || []
}

/**
 * Clear all caches (useful when switching sessions)
 */
export function clearCharacterCache(): void {
  characterCache.clear()
  sessionCharactersCache.clear()
}

/**
 * Remove a specific character from cache (useful after updates)
 */
export function invalidateCharacter(characterId: string): void {
  characterCache.delete(characterId)
}

/**
 * Find a character by player ID from cached session characters
 */
export function findCharacterByPlayerId(
  sessionId: string,
  playerId: string
): VTTCharacterData | null {
  const sessionChars = sessionCharactersCache.get(sessionId)
  if (!sessionChars) return null

  return sessionChars.find(c => c.playerId === playerId) || null
}

/**
 * Format ability modifier for display (e.g., "+3" or "-1")
 */
export function formatModifier(mod: number): string {
  return mod >= 0 ? `+${mod}` : `${mod}`
}

/**
 * Get ability name from abbreviation
 */
export function getAbilityName(abbrev: string): string {
  const names: Record<string, string> = {
    str: 'Strength',
    dex: 'Dexterity',
    con: 'Constitution',
    int: 'Intelligence',
    wis: 'Wisdom',
    cha: 'Charisma',
  }
  return names[abbrev.toLowerCase()] || abbrev.toUpperCase()
}

/**
 * Build the URL for the main platform's new-character creation page.
 * Opens in a new tab — no prefill (tracked: ac-mvp#225).
 */
export function getCharacterCreationUrl(): string {
  return `${getMainPlatformUrl()}/dashboard/characters/new`
}

/**
 * Fetch the GM's own characters from the platform.
 * Used by the "Link existing character" picker in the NPC token modal.
 */
export async function fetchGMCharacters(
  token: string
): Promise<GMCharacterSummary[]> {
  const platformUrl = getMainPlatformUrl()

  try {
    const response = await fetch(`${platformUrl}/api/characters`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (response.status === 401) {
      handleAuthExpired('fetchGMCharacters')
      return []
    }

    if (!response.ok) {
      console.warn('[CharacterService] fetchGMCharacters failed:', response.status)
      return []
    }

    const data = await response.json()
    // The platform returns { characters: [...] } or a plain array depending on version
    const list: any[] = Array.isArray(data) ? data : data.characters ?? []
    return list.map((c: any) => ({
      id: c.id,
      name: c.name,
      imageUrl: c.imageUrl ?? null,
      tokenImageUrl: c.tokenImageUrl ?? null,
      system: c.system ?? 'dnd5e-2014',
      isTokenOnly: c.isTokenOnly ?? false,
      isMob: c.isMob ?? false,
      tokenClassification: c.tokenClassification ?? undefined,
      characterType: c.characterType ?? null,
    }))
  } catch (error) {
    console.error('[CharacterService] fetchGMCharacters error:', error)
    return []
  }
}

export interface CreateNpcCharacterPayload {
  name: string
  system: string
  imageUrl?: string | null
  tokenClassification: TokenClassification
  isMob: boolean
  derivedStats: {
    hp: { current: number; max: number }
    speed: number
    /** Optional at creation (#657) — omit to leave the token without an AC
     *  reading, same as before this field existed. */
    ac?: number
    initiative?: number
  }
}

/**
 * Create a lightweight NPC character on the platform (isTokenOnly: true).
 * Returns the new character's id, or null on failure.
 */
export async function createNpcCharacter(
  payload: CreateNpcCharacterPayload,
  token: string
): Promise<string | null> {
  const platformUrl = getMainPlatformUrl()

  try {
    const response = await fetch(`${platformUrl}/api/characters`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: payload.name,
        system: payload.system,
        systemData: {},
        derivedStats: payload.derivedStats,
        imageUrl: payload.imageUrl ?? null,
        isTokenOnly: true,
        isMob: payload.isMob,
        // Tag as an NPC sheet so the platform dashboard badges it "NPC"
        // instead of defaulting to "Player" (characterType=null).
        characterType: 'npc',
        tokenClassification: payload.tokenClassification,
      }),
    })

    if (response.status === 401) {
      handleAuthExpired('createNpcCharacter')
      return null
    }

    if (!response.ok) {
      console.warn('[CharacterService] createNpcCharacter failed:', response.status)
      return null
    }

    const data = await response.json()
    return data.id ?? null
  } catch (error) {
    console.error('[CharacterService] createNpcCharacter error:', error)
    return null
  }
}

/**
 * Clone a character on the platform. Used to "disconnect" a token from a
 * shared mob — after cloning, the token is repointed to the clone so the mob
 * and the token are no longer the same DB row.
 * Returns the new character's id, or null on failure.
 */
export async function cloneCharacter(
  characterId: string,
  token: string
): Promise<string | null> {
  const platformUrl = getMainPlatformUrl()

  try {
    const response = await fetch(
      `${platformUrl}/api/characters/${encodeURIComponent(characterId)}/clone`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      }
    )

    if (response.status === 401) {
      handleAuthExpired('cloneCharacter')
      return null
    }

    if (!response.ok) {
      console.warn('[CharacterService] cloneCharacter failed:', response.status)
      return null
    }

    const data = await response.json()
    return data.id ?? null
  } catch (error) {
    console.error('[CharacterService] cloneCharacter error:', error)
    return null
  }
}

/**
 * Upload an image to the VTT server's blob store and return its public URL.
 * Reuses the same endpoint as map background uploads.
 */
export async function uploadTokenImage(
  file: File,
  vttApiUrl: string,
  token: string
): Promise<string | null> {
  const formData = new FormData()
  formData.append('image', file)

  try {
    const response = await fetch(`${vttApiUrl}/api/maps/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // No Content-Type — let the browser set the multipart boundary.
      },
      body: formData,
    })

    if (!response.ok) {
      console.warn('[CharacterService] uploadTokenImage failed:', response.status)
      return null
    }

    const data = await response.json()
    return data.url ?? null
  } catch (error) {
    console.error('[CharacterService] uploadTokenImage error:', error)
    return null
  }
}
